<template>
  <tr class="wrapper-row">
    <th class="for-big-screens" colspan="7">
      <table>
        <tr>
          <td class="date">
            <FormDatePicker
              :value="entryDate"
              :min-date="dateFromString(startDate)"
              :max-date="dateFromString(endDate)"
              :placeholder="$t('expense.placeholders.selectDate')"
              @change="onDateChange"
            />
          </td>
          <td>
            <Checkbox
              v-model="isUnit"
              :readonly="!canEdit"
              class="tes-input"
              @change="onIsUnitChange($event)"
            />
          </td>
          <td>
            <InputFieldCurrency
              v-model.number="unitPrice"
              :readonly="!canEdit"
              :style="{ visibility: isUnit ? 'visible' : 'hidden' }"
              class="tes-currency-input"
              @change="onPriceOrQuantityChange()"
            />
          </td>
          <td>
            <InputField
              v-model.number="unitQuantity"
              :readonly="!canEdit"
              :style="{ visibility: isUnit ? 'visible' : 'hidden' }"
              :class="canEdit ? 'tes-number-input' : ''"
              @change="onPriceOrQuantityChange()"
            />
          </td>
          <td v-if="canEdit">
            <InputFieldCurrency
              v-model="amount"
              :readonly="!canEdit"
              class="tes-currency-input"
              @change="onAmountChange($event)"
            />
          </td>
          <td v-else>
            {{ amount | currency }}
          </td>
          <td>
            <textarea v-if="canEdit" v-model="entryNote" rows="1" @change="onEntryChange()" />
            <span v-else>{{ entryNote }}</span>
          </td>
          <td><font-awesome-icon icon="times" style="color:red" @click="onRemoveEntry()" /></td>
        </tr>
      </table>
    </th>
    <th colspan="7" class="for-small-screens">
      <table>
        <tr>
          <td>Date</td>
          <td colspan>
            <FormDatePicker
              :value="entryDate"
              :min-date="dateFromString(startDate)"
              :max-date="dateFromString(endDate)"
              :placeholder="$t('expense.placeholders.selectDate')"
              @change="onDateChange"
            />
          </td>
        </tr>
        <tr>
          <td>Is Unit</td>
          <td>
            <Checkbox
              v-model="isUnit"
              :readonly="!canEdit"
              class="tes-input"
              @change="onIsUnitChange($event)"
            />
          </td>
        </tr>
        <tr>
          <td>Unit Price</td>
          <td>
            <InputFieldCurrency
              v-model.number="unitPrice"
              :readonly="!canEdit"
              :style="{ visibility: isUnit ? 'visible' : 'hidden' }"
              class="tes-currency-input"
              @change="onPriceOrQuantityChange()"
            />
          </td>
        </tr>
        <tr>
          <td>Quantity</td>
          <td>
            <InputField
              v-model.number="unitQuantity"
              :readonly="!canEdit"
              :style="{ visibility: isUnit ? 'visible' : 'hidden' }"
              :class="canEdit ? 'tes-number-input' : ''"
              @change="onPriceOrQuantityChange()"
            />
          </td>
        </tr>
        <tr>
          <td>Amount</td>
          <td v-if="canEdit">
            <InputFieldCurrency
              v-model="amount"
              :readonly="!canEdit"
              class="tes-currency-input"
              @change="onAmountChange($event)"
            />
          </td>
          <td v-else>
            {{ amount | currency }}
          </td>
        </tr>
        <tr>
          <td>Note</td>
          <td>
            <textarea v-if="canEdit" v-model="entryNote" rows="1" @change="onEntryChange()" />
            <span v-else>{{ entryNote }}</span>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</template>
<script>
import Checkbox from '@/app/components/Checkbox';
import FormDatePicker from '@/app/components/FormDatePicker';
import { dateUtils } from '@/mixins/date-utils';
import InputField from '@/app/components/InputField';
import InputFieldCurrency from '@/app/components/InputFieldCurrency';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import store from '@/store';
import i18n from '@/i18n';

export default {
  name: 'ExpenseEntry',
  components: {
    Checkbox,
    FormDatePicker,
    InputField,
    InputFieldCurrency
  },
  mixins: [dateUtils],
  props: {
    value: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    showOnSmallScreens: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      id: this.value.id,
      entryDate: this.value.entryDate ? this.dateFromString(this.value.entryDate) : null,
      isUnit: this.value.isUnit,
      unitPrice: this.value.unitPrice,
      unitQuantity: this.value.unitQuantity,
      amount: this.value.amount,
      entryNote: this.value.entryNote
    };
  },
  methods: {
    onEntryChange: function() {
      this.$emit('entryChange', {
        id: this.id,
        entryDate: this.entryDate,
        isUnit: this.isUnit,
        unitPrice: this.unitPrice,
        unitQuantity: this.unitQuantity,
        amount: this.amount,
        entryNote: this.entryNote
      });
    },
    onIsUnitChange(event) {
      this.isUnit = event.checked;
      this.onEntryChange();
    },
    onPriceOrQuantityChange() {
      if (
        !isNaN(this.unitPrice) &&
        !isNaN(this.unitQuantity) &&
        this.unitPrice > 0 &&
        this.unitQuantity > 0
      ) {
        this.amount = (this.unitPrice * this.unitQuantity).toFixed(2);
      }
      this.onEntryChange();
    },
    onAmountChange(event) {
      this.amount = event.target.value;
      if (isNaN(this.amount)) {
        let avalue = store.dispatch(TolErrorsActions.SetError, {
          message: i18n.t('expense.errors.amountNotNumeric', { avalue })
        });
        event.srcElement.focus();
        return;
      }

      this.$store.dispatch(TolErrorsActions.ClearAll);
      this.onEntryChange();
    },
    onDateChange(newValue) {
      this.entryDate = newValue;
      this.onEntryChange();
    },
    onRemoveEntry() {
      this.$emit('removeEntry', {
        id: this.id
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

table {
  border-collapse: collapse;
  table-layout: fixed;
}

.tes-number-input,
.tes-currency-input {
  height: 3em;
  width: 5em;
  font-size: inherit;
  text-align: center;
}

.tes-number-input {
  border: 1px solid $input-border;
}

tr td {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $row-border;
}
.cell-wrapper {
  border: 0;
  padding: 0;
  border-collapse: collapse;
  width: 100%;
}

.wrapper-row {
  width: 100%;
}

td {
  padding: 5px;
}

td.date {
  font-weight: bold;
  text-align: left;
}

tr td:first-child {
  padding-left: $details-padding;
}
tr td:last-child {
  padding-right: $details-padding;
}

.for-small-screens {
  display: none;
}

@media only screen and (max-width: 768px) {
  * {
    font-size: 0.8rem;
  }

  tr th:first-child,
  tr td:first-child {
    padding-left: $responsive-details-padding;
  }
  tr th:last-child,
  tr td:last-child {
    padding-right: $responsive-details-padding;
  }

  .for-big-screens {
    display: none !important;
  }

  .for-small-screens {
    display: block !important;
    .label {
      width: 70px;
      background-color: $white;
    }
    tr td:first-of-type {
      width: 40%;
    }
  }
}
</style>
